import { Code, ConnectError } from '@bufbuild/connect';
import {
  getImageWithFallback,
  getMediaBasePath,
} from '../../../commonUtils/images';
import { AcademicYear_Enum } from '@protos/common/common.db_pb';
import {
  GilClassSectionInfo,
  GilGalleryItem,
  GilProjectInfo,
  GilProjectScore,
  GilProjectStatusEnum,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';
import { FileEnum } from '@protos/content_management/content.db_pb';

const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? `${hours} hr ${minutes % 60} min` : `${hours} hr`;
};

/** 3120 -> "52 min", 26700 -> "7 hr 25 min" */
export const formatDuration = (seconds = 0): string =>
  formatMinutes(Math.round(seconds / 60));

/**
 * Formats the parts of one duration and their total so the shown parts add up
 * to the shown total. Rounding each on its own does not: 30s + 11m 30s shows
 * as 1 min + 12 min against a 12 min total.
 *
 * The total is rounded as `formatDuration` would round it, so it matches a
 * total shown elsewhere. The parts are floored and the minutes still owed go
 * to the parts with the largest remainders.
 */
export const formatDurationBreakdown = (
  partsSec: number[]
): { parts: string[]; total: string } => {
  const exact = partsSec.map((seconds) => Math.max(0, seconds || 0) / 60);
  const totalMinutes = Math.round(exact.reduce((sum, value) => sum + value, 0));
  const minutes = exact.map(Math.floor);
  let owed = totalMinutes - minutes.reduce((sum, value) => sum + value, 0);
  exact
    .map((value, index) => ({ index, remainder: value - minutes[index] }))
    .sort((a, b) => b.remainder - a.remainder)
    .forEach(({ index }) => {
      if (owed > 0) {
        minutes[index] += 1;
        owed -= 1;
      }
    });
  return {
    parts: minutes.map(formatMinutes),
    total: formatMinutes(totalMinutes),
  };
};

/** The project's own title, e.g. "Smart Street Light". */
export const projectLabel = (project?: GilProjectInfo) =>
  project?.projectTitle ?? '';

/** "18/20", or "—" until the teacher has marked the student. */
export const formatScore = (score?: GilProjectScore) =>
  score?.isGraded ? `${score.obtainedMarks ?? 0}/${score.maxMarks ?? 0}` : '—';

/** 84.5 -> "85%", absent -> "—" */
export const formatPercent = (value?: number) =>
  value === undefined ? '—' : `${Math.round(value)}%`;

export const STATUS_LABEL: Record<GilProjectStatusEnum, string> = {
  [GilProjectStatusEnum.GIL_PROJECT_STATUS_UNDEFINED]: '—',
  [GilProjectStatusEnum.GIL_PROJECT_STATUS_NOT_STARTED]: 'Not Started',
  [GilProjectStatusEnum.GIL_PROJECT_STATUS_IN_PROGRESS]: 'In Progress',
  [GilProjectStatusEnum.GIL_PROJECT_STATUS_COMPLETED]: 'Completed',
};

/** AY_2026_2027 -> "2026–27" */
export const formatAcademicYear = (value?: AcademicYear_Enum) => {
  const match =
    value !== undefined
      ? AcademicYear_Enum[value]?.match(/^AY_(\d{4})_\d{2}(\d{2})$/)
      : null;
  return match ? `${match[1]}–${match[2]}` : '';
};

/** "Class VI A" - class and section as the school stores them. */
export const formatClassSection = (classSection?: GilClassSectionInfo) =>
  [classSection?.className, classSection?.sectionName]
    .filter(Boolean)
    .join(' ');

export const formatDate = (date?: { toDate: () => Date }) =>
  date
    ? date.toDate().toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

/** "01 Oct 2026, 2:05 pm" - for "updated at" stamps. */
export const formatDateTime = (date?: { toDate: () => Date }) =>
  date
    ? date.toDate().toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

/** 0.4 -> "< 1 day", 2.5 -> "2.5 days", absent -> "—" */
export const formatDays = (days?: number) => {
  if (days === undefined) return '—';
  if (days < 1) return '< 1 day';
  const rounded = Math.round(days * 10) / 10;
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
};

/** 5 -> "5 hr", 50 -> "2.1 days", absent -> "—" */
export const formatHours = (hours?: number) => {
  if (hours === undefined) return '—';
  if (hours < 1) return '< 1 hr';
  return hours < 48 ? `${Math.round(hours)} hr` : formatDays(hours / 24);
};

/** An int64 id from a route or query param; anything but a positive whole number is treated as missing. */
export const parseIdParam = (value?: string | null) =>
  value && /^[1-9]\d*$/.test(value) ? BigInt(value) : undefined;

/** Share of `part` in `total` as 0-100, for progress bars. */
export const toPercent = (part = 0, total = 0) =>
  total > 0 ? Math.min(100, Math.max(0, (part / total) * 100)) : 0;

export interface ReportErrorCopy {
  /** Shown on PermissionDenied - defaults to the teacher/class wording. */
  permissionDenied?: string;
}

/** Maps the Connect error codes the GIL report APIs return to screen copy. */
export const reportErrorMessage = (error: unknown, copy?: ReportErrorCopy) => {
  const connectError = error instanceof ConnectError ? error : undefined;
  switch (connectError?.code) {
    case Code.NotFound:
      return 'This project is no longer available';
    case Code.PermissionDenied:
      return copy?.permissionDenied ?? "You don't have access to this class";
    case Code.Unauthenticated:
      return 'Your session has expired. Please log in again';
    default:
      //~ invalid_argument / failed_precondition / internal are bugs, not user
      //~ states - keep the server's reason visible so they can be reported.
      console.error('GIL report request failed', {
        code: connectError ? Code[connectError.code] : undefined,
        message: connectError?.rawMessage ?? (error as Error)?.message,
        error,
      });
      return connectError
        ? `Something went wrong while loading this report (${
            Code[connectError.code]
          }: ${connectError.rawMessage})`
        : 'Something went wrong while loading this report';
  }
};

/** One renderable image or video, whichever response it came from. */
export interface GalleryMedia {
  key: string;
  /** Best-guess url, shown until `resolveMediaUrl` has probed the buckets. */
  url: string;
  /** The stored path, resolved against the buckets before it is rendered. */
  path: string;
  isVideo: boolean;
  fileName: string;
  caption?: string;
  subCaption?: string;
}

/**
 * The bucket order the rest of the project flow reads student uploads through.
 * `downloadUrl` is last: it is only set on some uploads, and an unsigned one
 * 403s against the private user-data bucket.
 */
const uploadPath = (upload: CloudUpload) =>
  upload.gcpFileUrl ||
  upload.previewUrl ||
  upload.signedUrl ||
  upload.downloadUrl ||
  '';

const uploadUrl = (upload: CloudUpload) =>
  getMediaBasePath(uploadPath(upload), 'userDataBucket');

/**
 * The url a photo or video actually plays from: the same HEAD probe with
 * credentials, and the same bucket fallback, the project evidence dialogs use.
 */
export const resolveMediaUrl = (item: GalleryMedia) =>
  getImageWithFallback(item.path, 'userDataBucket', 'schoolnetCdnImagesBucket');

const isRenderable = (upload?: CloudUpload): upload is CloudUpload =>
  !!upload &&
  !upload.isDeleted &&
  (upload.fileType === FileEnum.FILE_TYPE_IMAGE ||
    upload.fileType === FileEnum.FILE_TYPE_VIDEO) &&
  !!uploadPath(upload);

export const uploadsToMedia = (
  uploads: CloudUpload[] = [],
  caption?: string
): GalleryMedia[] =>
  uploads.filter(isRenderable).map((upload, index) => ({
    key: `${upload.gcpFileUrl || upload.fileName}-${index}`,
    url: uploadUrl(upload),
    path: uploadPath(upload),
    isVideo: upload.fileType === FileEnum.FILE_TYPE_VIDEO,
    fileName: upload.fileName,
    caption,
  }));

export const galleryItemsToMedia = (
  items: GilGalleryItem[] = [],
  showProject = true
): GalleryMedia[] =>
  items
    .filter((item) => isRenderable(item.upload))
    .map((item, index) => {
      const upload = item.upload as CloudUpload;
      return {
        key: `${item.assessmentId}-${upload.gcpFileUrl || upload.fileName}-${index}`,
        url: uploadUrl(upload),
        path: uploadPath(upload),
        isVideo: upload.fileType === FileEnum.FILE_TYPE_VIDEO,
        fileName: upload.fileName,
        caption: showProject ? item.projectTitle : item.groupName,
        subCaption: showProject ? item.groupName : undefined,
      };
    });
